import { Banner, Select, Text, useToast } from '@chatsdet/ui';
import { useAuth } from '../../../contexts/AuthContext';
import type { TicketingProviderSummary } from '../../../hooks/useTicketingProviders';
import { getTicketingProviderLabel } from '../../../utils/ticketingProviders';
import { useSetDefaultTicketSource, type DefaultTicketSource } from '../useTickets';

/**
 * Picks which connected ticket provider is the org's default: the one this page configures and
 * the one tickets are filed in. Admins choose; everyone else sees the choice read-only.
 */
export function TicketSourceSelect({
  source,
  providers,
}: {
  source: DefaultTicketSource;
  providers: TicketingProviderSummary[];
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === 'admin';
  const save = useSetDefaultTicketSource();

  return (
    <div>
      {save.error && <Banner variant="error">{save.error.message}</Banner>}
      <Select
        label="Ticket source"
        selectedKey={source.defaultProviderId}
        items={source.connected.map((id) => ({
          id,
          label: getTicketingProviderLabel(id, providers),
        }))}
        placeholder="Choose a ticket source..."
        isDisabled={!isAdmin || save.isPending}
        description="Where Cofactor files tickets, and the provider configured below."
        onSelectionChange={(key) => {
          if (!key || String(key) === source.defaultProviderId) return;
          save.mutate(String(key), {
            onSuccess: () => toast.success('Default ticket source saved'),
          });
        }}
      />
      {!isAdmin && (
        <Text size="sm" className="text-muted-foreground mt-1">
          Only admins can change the ticket source.
        </Text>
      )}
    </div>
  );
}
